import { useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button'
import Info from '@/components/Info'
import Input from '@/components/Input'
import Modal from '@/components/Modal'
import TaxInput from '@/components/TaxInput'
import {
  getVatIdCountryCodeFromValue,
  getVatIdErrorTranslationKey,
  normalizeVatId,
  validateVatId
} from '@/components/TaxInput/vatIdValidation'
import useCheckout from '@/features/Checkout/hooks/useCheckout'
import useCompanyVatLookup, { CompanyVatFill } from '@/features/Checkout/hooks/useCompanyVatLookup'

import {
  InvoiceTypeTab,
  InvoiceTypeToggle,
  TaxIdActions,
  TaxIdFormGrid,
  TaxIdModalTitle,
  TaxIdModalWrapper,
  VatLookupCell,
  VatLookupMessage
} from './TaxIdModal.styles'

type InvoiceField = 'nip' | 'name' | 'city' | 'street' | 'houseNumber' | 'postalCode'

interface InvoiceFormValues {
  nip: string
  name: string
  city: string
  street: string
  houseNumber: string
  postalCode: string
}

const maxLengths: Record<InvoiceField, number> = {
  nip: 20,
  name: 256,
  city: 256,
  street: 256,
  houseNumber: 256,
  postalCode: 10
}

const fillableFields = ['name', 'city', 'street', 'houseNumber', 'postalCode'] as const

const TaxIdModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation()
  const { companyData, setCompanyData } = useCheckout()
  const [personalInvoice, setPersonalInvoice] = useState(false)
  const initialVatCountryCode = useMemo(() => getVatIdCountryCodeFromValue(companyData?.nip), [companyData?.nip])
  const [vatCountryCode, setVatCountryCode] = useState(initialVatCountryCode)

  const defaultValues = useMemo<InvoiceFormValues>(
    () => ({
      nip: normalizeVatId(companyData?.nip ?? '', initialVatCountryCode),
      name: companyData?.name ?? '',
      city: companyData?.city ?? '',
      street: companyData?.street ?? '',
      houseNumber: companyData?.houseNumber ?? '',
      postalCode: companyData?.postalCode ?? ''
    }),
    [companyData, initialVatCountryCode]
  )

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { errors }
  } = useForm<InvoiceFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues
  })

  const lastAutoFillRef = useRef<CompanyVatFill | null>(null)

  const applyCompanyFill = (fill: CompanyVatFill) => {
    const appliedValues: CompanyVatFill = {}

    fillableFields.forEach(field => {
      const value = fill[field]
      if (value) {
        const limitedValue = value.slice(0, maxLengths[field])
        appliedValues[field] = limitedValue
        setValue(field, limitedValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
      }
    })

    lastAutoFillRef.current = appliedValues
  }

  // Po zmianie NIP-u dane pobrane dla poprzedniego numeru są nieaktualne — czyścimy je,
  // ale tylko w polach, których użytkownik nie nadpisał ręcznie po auto-fillu.
  const clearStaleAutoFill = () => {
    const lastAutoFill = lastAutoFillRef.current
    if (!lastAutoFill) {
      return
    }

    lastAutoFillRef.current = null
    fillableFields.forEach(field => {
      const autoFilledValue = lastAutoFill[field]
      if (autoFilledValue && getValues(field) === autoFilledValue) {
        setValue(field, '', { shouldDirty: true, shouldValidate: false })
      }
    })
  }

  const { lookup, isLoading: isLookupLoading, feedback: lookupFeedback, clearFeedback } = useCompanyVatLookup({ onFill: applyCompanyFill })

  const requiredMessage = t('fieldRequired')
  const textFieldRules = {
    required: requiredMessage,
    validate: (value: string) => Boolean(value.trim()) || requiredMessage
  }

  const updateFieldValue = (field: InvoiceField, value: string) => {
    setValue(field, value.slice(0, maxLengths[field]), { shouldDirty: true, shouldTouch: true, shouldValidate: false })
  }

  const updateVatIdValue = (value: string) => {
    clearFeedback()
    clearStaleAutoFill()
    setValue('nip', value.slice(0, maxLengths.nip), { shouldDirty: true, shouldTouch: true, shouldValidate: false })
  }

  const handleCountryChange = (countryCode: string) => {
    clearFeedback()
    clearStaleAutoFill()
    setVatCountryCode(countryCode)
  }

  const handleFetchCompanyData = async () => {
    const validation = validateVatId(getValues('nip'), vatCountryCode)

    if (!validation.isValid) {
      await trigger('nip')
      return
    }

    await lookup(validation.prefixed)
  }

  const validateVatIdValue = (value: string) => {
    const validation = validateVatId(value, vatCountryCode)
    const translationKey = getVatIdErrorTranslationKey(validation.error)
    return validation.isValid || t(translationKey ?? 'invalidTaxId')
  }

  const handleSave = (values: InvoiceFormValues) => {
    const vatIdValidation = validateVatId(values.nip, vatCountryCode)

    setCompanyData({
      nip: vatIdValidation.prefixed,
      name: values.name.trim(),
      city: values.city.trim(),
      street: values.street.trim(),
      houseNumber: values.houseNumber.trim(),
      postalCode: values.postalCode.trim()
    })
    onClose()
  }

  return (
    <Modal onClose={onClose} variant="default" size="wide">
      <TaxIdModalWrapper>
        <TaxIdModalTitle>{t('invoiceDataTitle')}</TaxIdModalTitle>
        <InvoiceTypeToggle>
          <InvoiceTypeTab type="button" $active={!personalInvoice} onClick={() => setPersonalInvoice(false)}>
            {t('invoiceTypeVat')}
          </InvoiceTypeTab>
          <InvoiceTypeTab type="button" $active={personalInvoice} onClick={() => setPersonalInvoice(true)}>
            {t('invoiceTypePersonal')}
          </InvoiceTypeTab>
        </InvoiceTypeToggle>
        {personalInvoice && (
          <>
            <Info>{t('personalInvoiceKioskInfo')}</Info>
            <TaxIdActions>
              <Button variant="primary" onClick={onClose}>
                {t('understood')}
              </Button>
            </TaxIdActions>
          </>
        )}
        {!personalInvoice && (
          <>
        <TaxIdFormGrid>
          <Controller
            control={control}
            name="nip"
            rules={{
              required: t('taxIdRequired'),
              validate: validateVatIdValue
            }}
            render={({ field }) => (
              <TaxInput
                ref={field.ref}
                name={field.name}
                value={field.value}
                countryCode={vatCountryCode}
                label={t('taxIdLabel')}
                onBlur={field.onBlur}
                onCountryChange={handleCountryChange}
                onChange={updateVatIdValue}
                error={errors.nip?.message}
              />
            )}
          />
          <VatLookupCell>
            <Button variant="secondary" disabled={isLookupLoading} onClick={handleFetchCompanyData}>
              {isLookupLoading ? t('vatLookupLoading') : t('fetchCompanyData')}
            </Button>
          </VatLookupCell>
          {lookupFeedback && <VatLookupMessage $variant={lookupFeedback.type}>{t(lookupFeedback.messageKey)}</VatLookupMessage>}
          <Controller
            control={control}
            name="name"
            rules={textFieldRules}
            render={({ field }) => (
              <Input
                {...field}
                label={t('invoiceNameLabel')}
                placeholder={t('invoiceNamePlaceholder')}
                keyboard
                maxLength={maxLengths.name}
                onBlur={field.onBlur}
                onChange={value => updateFieldValue('name', value)}
                error={errors.name?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="city"
            rules={textFieldRules}
            render={({ field }) => (
              <Input
                {...field}
                label={t('invoiceCityLabel')}
                placeholder={t('invoiceCityPlaceholder')}
                keyboard
                maxLength={maxLengths.city}
                onBlur={field.onBlur}
                onChange={value => updateFieldValue('city', value)}
                error={errors.city?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="street"
            rules={textFieldRules}
            render={({ field }) => (
              <Input
                {...field}
                label={t('invoiceStreetLabel')}
                placeholder={t('invoiceStreetPlaceholder')}
                keyboard
                maxLength={maxLengths.street}
                onBlur={field.onBlur}
                onChange={value => updateFieldValue('street', value)}
                error={errors.street?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="houseNumber"
            rules={{
              required: requiredMessage,
              validate: (value: string) => Boolean(value.trim()) || requiredMessage
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('invoiceBuildingNumberLabel')}
                placeholder={t('invoiceBuildingNumberPlaceholder')}
                type="numericAndText"
                keyboard
                maxLength={maxLengths.houseNumber}
                onBlur={field.onBlur}
                onChange={value => updateFieldValue('houseNumber', value)}
                error={errors.houseNumber?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="postalCode"
            rules={{
              required: requiredMessage,
              validate: (value: string) => Boolean(value.trim()) || requiredMessage
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('invoicePostalCodeLabel')}
                placeholder={t('invoicePostalCodePlaceholder')}
                type="numericAndText"
                keyboard
                maxLength={maxLengths.postalCode}
                onBlur={field.onBlur}
                onChange={value => updateFieldValue('postalCode', value)}
                error={errors.postalCode?.message}
              />
            )}
          />
        </TaxIdFormGrid>
        <TaxIdActions>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit(handleSave)}>
            {t('saveInvoiceData')}
          </Button>
        </TaxIdActions>
          </>
        )}
      </TaxIdModalWrapper>
    </Modal>
  )
}

export default TaxIdModal
